import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { sendSupportMessage } from '../services/emailService';

const CATEGORIES = [
  { id: 'question', label: 'General Question' },
  { id: 'feedback', label: 'Feedback / Suggestion' },
  { id: 'issue', label: 'Report an Issue' },
  { id: 'billing', label: 'Billing / Payment' },
];

export default function SupportPage() {
  const { currentUser, userProfile } = useAuth();
  const [form, setForm] = useState({ category: 'question', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.message.trim()) {
      toast.error('Please fill in all fields.');
      return;
    }
    setSending(true);
    try {
      await sendSupportMessage({
        from: currentUser?.email || 'anonymous',
        name: userProfile
          ? `${userProfile.firstName || ''} ${userProfile.lastName || ''}`.trim()
          : currentUser?.displayName || '',
        phone: userProfile?.phone || '',
        category: form.category,
        subject: form.subject.trim(),
        message: form.message.trim(),
      });
      setSent(true);
      toast.success('Message sent! We\'ll get back to you soon.');
    } catch {
      toast.error('Failed to send. Please email admin@thehubcrookwell.com.au directly.');
    }
    setSending(false);
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-gray-50 py-10">
        <div className="max-w-lg mx-auto px-4 text-center">
          <div className="card p-10 space-y-4">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-slate-800">Message Sent</h2>
            <p className="text-sm text-slate-500">
              Thanks for reaching out. We'll get back to you at <strong>{currentUser?.email}</strong> as soon as possible.
            </p>
            <div className="flex justify-center gap-3 pt-2">
              <button onClick={() => { setSent(false); setForm({ category: 'question', subject: '', message: '' }); }} className="btn-secondary text-sm">
                Send Another
              </button>
              <Link to="/dashboard" className="btn-primary text-sm">Back to Dashboard</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10">
      <div className="max-w-lg mx-auto px-4">
        <div className="mb-6">
          <Link to="/dashboard" className="text-sm text-slate-400 hover:text-slate-600">&larr; Back to Dashboard</Link>
        </div>
        <div className="card p-6 space-y-5">
          <div>
            <h1 className="text-xl font-bold text-slate-800">Contact Support</h1>
            <p className="text-sm text-slate-500 mt-1">
              Have a question, feedback, or need help? Send us a message and we'll get back to you.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label text-sm">Category</label>
              <select
                className="input-field"
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              >
                {CATEGORIES.map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="label text-sm">Subject *</label>
              <input
                className="input-field"
                placeholder="What's this about?"
                value={form.subject}
                onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                required
              />
            </div>

            <div>
              <label className="label text-sm">Message *</label>
              <textarea
                className="input-field resize-none"
                rows={5}
                placeholder="Tell us more..."
                value={form.message}
                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                required
              />
            </div>

            {currentUser && (
              <p className="text-xs text-slate-400">
                We'll reply to <strong>{currentUser.email}</strong>
              </p>
            )}

            <button
              type="submit"
              disabled={sending}
              className="btn-primary w-full disabled:opacity-50"
            >
              {sending ? 'Sending…' : 'Send Message'}
            </button>
          </form>

          <div className="pt-3 border-t border-gray-100 text-center">
            <p className="text-xs text-slate-400">
              Or email us directly at{' '}
              <a href="mailto:admin@thehubcrookwell.com.au" className="text-brand-500 hover:underline">
                admin@thehubcrookwell.com.au
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
